import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type AdministrativeRegionView } from '@vello/api-client';
import { Badge, Button, Cluster, EmptyState, Field, Notice, Select, Stack, Table, Text, type Column } from '@vello/web-theme';
import {
  REGION_IMPACT_BANNER_PAGE,
  REGION_IMPACT_BROADCAST_PAGE,
  applyRegionChange,
  marketsOfRegion,
  openBroadcastCount,
  regionConsoleCountries,
  regionConsoleRows,
  regionSuffix,
  stateBannerCount,
  updateRegion,
} from '../../src/index';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { usePlaceNames } from '../i18n/scope-name';
import { useVocab } from '../i18n/vocab';
import { useAdmin } from '../session';
import { RegionRenameDialog } from './region-rename-dialog';

/**
 * Operações › Mercados › Estados (ADR-0124): the states of a country, with their status. A manager renames a state (the name every screen shows) and retires or
 * reactivates it. A retired state keeps the cities and the access grants that already name it and takes nothing new (a city, a grant, a broadcast audience, a banner,
 * a TV panel) - the server refuses those, the registry here only says so. Every change is audited server side; the catalog the rest of the console reads is refreshed
 * at once (`refreshRegions`), so the names in the selector and the state selects follow.
 */
export function RegionsConsole(): ReactElement {
  const { runtime, markets, scope, refreshRegions } = useAdmin();
  const { t } = useT();
  const { v, fail, explain } = useVocab();
  const { confirm } = useConsole();
  const place = usePlaceNames();

  const [all, setAll] = useState<readonly AdministrativeRegionView[] | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [country, setCountry] = useState<string>('');
  const [msg, setMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [renaming, setRenaming] = useState<AdministrativeRegionView | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.markets.regions();
    if (res.ok) {
      setAll(res.value.regions);
      setLoadFailed(false);
    } else setLoadFailed(true);
  }, [runtime]);
  useEffect(() => {
    void load();
  }, [load]);

  const countries = useMemo(() => regionConsoleCountries(all ?? []), [all]);
  // the selected country: the one the operator chose, else the top-bar scope's country, else the first that has states
  useEffect(() => {
    if (countries.length === 0 || countries.includes(country)) return;
    const preferred = scope.current.country?.toUpperCase() ?? scope.current.region?.slice(0, 2).toUpperCase();
    setCountry(preferred !== undefined && countries.includes(preferred) ? preferred : (countries[0] as string));
  }, [countries, country, scope]);
  const rows = useMemo(() => regionConsoleRows(all ?? [], country), [all, country]);

  const explained = (error: Parameters<typeof explain>[0]): string => explain(error) ?? t('rgn.failed', { code: fail(error) });

  const apply = async (region: AdministrativeRegionView, change: { readonly name?: string; readonly status?: AdministrativeRegionView['status'] }): Promise<{ readonly ok: true; readonly region: AdministrativeRegionView } | { readonly ok: false; readonly text: string }> => {
    const res = await updateRegion(runtime, region.code, change);
    if (!res.ok) return { ok: false, text: explained(res.error) };
    setAll((list) => (list === undefined ? list : applyRegionChange(list, res.value.region)));
    await refreshRegions?.();
    return { ok: true, region: res.value.region };
  };

  const onRename = async (name: string): Promise<void> => {
    if (renaming === undefined) return;
    setBusy(true);
    setRenameError(undefined);
    const done = await apply(renaming, { name });
    setBusy(false);
    if (done.ok) {
      setRenaming(undefined);
      setMsg({ tone: 'success', text: t('rgn.done.renamed', { name: done.region.name }) });
    } else setRenameError(done.text);
  };

  /**
   * What retiring a state leaves running: the broadcasts still queued or sending that reach it (read with `region=`) and the banners live or scheduled for it or its
   * cities. A read that fails, or may have been cut by the server's page, is said so - the console never prints a zero it did not see.
   */
  const impactOf = async (region: AdministrativeRegionView): Promise<string> => {
    const [campaigns, banners] = await Promise.all([
      runtime.broadcast.list({ region: region.code }, { limit: REGION_IMPACT_BROADCAST_PAGE }),
      runtime.banners.list({ includeExpired: false }),
    ]);
    const lines: string[] = [];
    let running = 0;
    if (campaigns.ok && campaigns.value.campaigns.length < REGION_IMPACT_BROADCAST_PAGE) {
      running = openBroadcastCount(campaigns.value.campaigns);
      lines.push(t('rgn.retire.impact.broadcasts', { count: running }));
    } else lines.push(t('rgn.retire.impact.broadcasts.unknown'));
    if (banners.ok && banners.value.banners.length < REGION_IMPACT_BANNER_PAGE) {
      const live = stateBannerCount(banners.value.banners, region.code, markets, Date.now());
      running += live;
      lines.push(t('rgn.retire.impact.banners', { count: live }));
    } else lines.push(t('rgn.retire.impact.banners.unknown'));
    if (running > 0) lines.push(t('rgn.retire.impact.keep'));
    return lines.join(' ');
  };

  const onToggle = async (region: AdministrativeRegionView): Promise<void> => {
    setMsg(undefined);
    const retire = region.status === 'ACTIVE';
    const impact = retire ? await impactOf(region) : '';
    const answer = await confirm({
      title: t(retire ? 'rgn.retire.title' : 'rgn.reactivate.title', { name: region.name }),
      body: retire ? `${t('rgn.retire.body', { count: marketsOfRegion(region.code, markets).length })} ${impact}` : t('rgn.reactivate.body'),
      confirmLabel: t(retire ? 'rgn.retire.confirm' : 'rgn.reactivate.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: retire ? 'danger' : 'primary',
      icon: 'badgeCheck',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    const done = await apply(region, { status: retire ? 'RETIRED' : 'ACTIVE' });
    setBusy(false);
    setMsg(done.ok ? { tone: 'success', text: t(retire ? 'rgn.done.retired' : 'rgn.done.reactivated', { name: done.region.name }) } : { tone: 'danger', text: done.text });
  };

  const columns: readonly Column<AdministrativeRegionView>[] = [
    { key: 'name', header: t('rgn.col.name'), render: (r) => <strong>{r.name}</strong> },
    { key: 'code', header: t('rgn.col.code'), render: (r) => <span className="tnum">{regionSuffix(r.code)}</span> },
    { key: 'status', header: t('rgn.col.status'), render: (r) => <Badge tone={r.status === 'ACTIVE' ? 'success' : 'neutral'}>{v('region.status', r.status)}</Badge> },
    { key: 'markets', header: t('rgn.col.markets'), numeric: true, render: (r) => <span className="tnum">{marketsOfRegion(r.code, markets).length}</span> },
    {
      key: 'action',
      header: t('rgn.col.action'),
      render: (r) => (
        <Cluster gap={2} wrap>
          <Button variant="ghost" disabled={busy} aria-label={t('rgn.action.rename.of', { name: r.name })} onClick={() => { setRenameError(undefined); setRenaming(r); }}>
            {t('rgn.action.rename')}
          </Button>
          <Button variant="ghost" disabled={busy} aria-label={t(r.status === 'ACTIVE' ? 'rgn.action.retire.of' : 'rgn.action.reactivate.of', { name: r.name })} onClick={() => void onToggle(r)}>
            {t(r.status === 'ACTIVE' ? 'rgn.action.retire' : 'rgn.action.reactivate')}
          </Button>
        </Cluster>
      ),
    },
  ];

  return (
    <Stack gap={4} data-testid="rgn-console">
      <Text as="p" tone="secondary">{t('rgn.hint')}</Text>
      {loadFailed && <Notice tone="danger">{t('rgn.loadFailed')}</Notice>}
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {all !== undefined && countries.length === 0 ? (
        <EmptyState title={t('rgn.noCountries')} />
      ) : (
        <>
          {countries.length > 1 && (
            <Field label={t('rgn.field.country')} htmlFor="rgn-country">
              <Select id="rgn-country" value={country} onChange={(e) => setCountry(e.target.value)}>
                {countries.map((cc) => <option key={cc} value={cc}>{place.country(cc)}</option>)}
              </Select>
            </Field>
          )}
          {all !== undefined && <Table columns={columns} rows={rows} rowKey={(r) => r.code} empty={<Notice tone="neutral">{t('rgn.empty')}</Notice>} />}
        </>
      )}
      <RegionRenameDialog region={renaming} busy={busy} error={renameError} onCancel={() => setRenaming(undefined)} onConfirm={(name) => void onRename(name)} />
    </Stack>
  );
}
